import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { useTranslation } from './i18n/LanguageContext'
import { useSmoothScroll } from './hooks/useSmoothScroll'
import { useAuth } from './hooks/useAuth'
import ProtectedRoute from './components/Auth/ProtectedRoute'
import Navbar from './components/Layout/Navbar'
import Footer from './components/Layout/Footer'
import Loader from './components/Loader/Loader'
import DialogContainer from './components/Dialog/DialogContainer'

// Lazy load pages - solo se descargan cuando el usuario visita la ruta
// Esto reduce la carga inicial y mejora la velocidad en móvil y escritorio
const Home = lazy(() => import('./pages/Home'))
const AboutUs = lazy(() => import('./pages/AboutUs'))
const Contact = lazy(() => import('./pages/Contact'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Login = lazy(() => import('./pages/Auth/Login'))
const Register = lazy(() => import('./pages/Auth/Register'))
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'))
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'))
const DashboardHome = lazy(() => import('./pages/Dashboard/DashboardHome'))
const Checklist = lazy(() => import('./pages/Dashboard/Checklist'))
const BudgetManager = lazy(() => import('./pages/Dashboard/BudgetManager'))
const GuestManager = lazy(() => import('./pages/Dashboard/GuestManager'))
const GuestManagerIndex = lazy(() => import('./pages/Dashboard/GuestManager/GuestManagerIndex'))
const TheLifesaverLink = lazy(() => import('./pages/Dashboard/GuestManager/TheLifesaverLink'))
const RSVPFormBuilder = lazy(() => import('./pages/Dashboard/GuestManager/RSVPFormBuilder'))
const CraftYourInvite = lazy(() => import('./pages/Dashboard/GuestManager/CraftYourInvite'))
const GentleReminderTime = lazy(() => import('./pages/Dashboard/GuestManager/GentleReminderTime'))
const WhoSitsWhere = lazy(() => import('./pages/Dashboard/GuestManager/WhoSitsWhere'))
const SupplierManager = lazy(() => import('./pages/Dashboard/SupplierManager'))
const SupplierManagerIndex = lazy(() => import('./pages/Dashboard/SupplierManager/SupplierManagerIndex'))
const NumbersPlease = lazy(() => import('./pages/Dashboard/SupplierManager/NumbersPlease'))
const TheChosenOnes = lazy(() => import('./pages/Dashboard/SupplierManager/TheChosenOnes'))
const TimelineCreator = lazy(() => import('./pages/Dashboard/TimelineCreator'))
const Lists = lazy(() => import('./pages/Dashboard/Lists'))
const ListsIndex = lazy(() => import('./pages/Dashboard/Lists/ListsIndex'))
const MustCaptureMoments = lazy(() => import('./pages/Dashboard/Lists/MustCaptureMoments'))
const TheSoundtrack = lazy(() => import('./pages/Dashboard/Lists/TheSoundtrack'))
const BloomBoard = lazy(() => import('./pages/Dashboard/Lists/BloomBoard'))
const WeddingCargo = lazy(() => import('./pages/Dashboard/Lists/WeddingCargo'))
const Profile = lazy(() => import('./pages/Dashboard/Profile'))
const LifesaverForm = lazy(() => import('./pages/Public/LifesaverForm'))
const RSVPInvitePage = lazy(() => import('./pages/Public/RSVPInvitePage'))
const RSVPSearch = lazy(() => import('./pages/Public/RSVPSearch'))
const RSVPForm = lazy(() => import('./pages/Public/RSVPForm'))

function App() {
  const { loading: authLoading } = useAuth()
  const location = useLocation()
  const { t, language } = useTranslation()
  useSmoothScroll()

  const isDashboard = location.pathname.startsWith('/dashboard')

  useEffect(() => {
    // 1. Update HTML lang attribute
    document.documentElement.lang = language || 'en'
    
    const isEs = language === 'es'
    let title = ''
    let description = ''
    let keywords = ''

    const path = location.pathname

    if (path.startsWith('/dashboard')) {
      title = isEs ? 'Panel de Control de Boda | The UNFILTERED COLLECTIVE' : 'Wedding Dashboard | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Gestiona el presupuesto, proveedores, invitados y cronograma de tu boda.' 
        : 'Manage your wedding budget, vendors, guests, and timeline.'
    } else if (path === '/about-us') {
      title = isEs 
        ? 'Nosotros - Por qué elegirnos | The UNFILTERED COLLECTIVE' 
        : 'About Us - Why Choose Us | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Conoce a los profesionales de bodas detrás de nuestra plataforma de planificación sin filtros.' 
        : 'Meet the wedding professionals behind our unfiltered wedding planning platform.'
    } else if (path === '/contact') {
      title = isEs ? 'Contacto | The UNFILTERED COLLECTIVE' : 'Contact Us | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Ponte en contacto con el equipo de The UNFILTERED COLLECTIVE.' 
        : 'Get in touch with the team at The UNFILTERED COLLECTIVE.'
    } else if (path === '/pricing') {
      title = isEs 
        ? 'Precios y Planes Gratuitos | The UNFILTERED COLLECTIVE' 
        : 'Pricing and Free Plans | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Todas nuestras herramientas de planificación de bodas son gratis. Conoce los planes.' 
        : 'All our wedding planning tools are currently free. Explore our packages.'
    } else if (path === '/login') {
      title = isEs ? 'Iniciar Sesión | The UNFILTERED COLLECTIVE' : 'Login | The UNFILTERED COLLECTIVE'
      description = isEs ? 'Inicia sesión en tu cuenta de planificación de bodas.' : 'Log in to your wedding planning account.'
    } else if (path === '/register') {
      title = isEs 
        ? 'Registrarse - Empieza a Organizar tu Boda | The UNFILTERED COLLECTIVE' 
        : 'Sign Up - Start Planning Your Wedding | The UNFILTERED COLLECTIVE'
      description = isEs ? 'Crea tu cuenta gratis y empieza a organizar tu boda hoy mismo.' : 'Create your free account and start planning your wedding today.'
    } else if (path === '/forgot-password') {
      title = isEs ? 'Recuperar Contraseña | The UNFILTERED COLLECTIVE' : 'Forgot Password | The UNFILTERED COLLECTIVE'
      description = isEs ? 'Recupera la contraseña de tu cuenta.' : 'Recover your account password.'
    } else if (path === '/reset-password') {
      title = isEs ? 'Restablecer Contraseña | The UNFILTERED COLLECTIVE' : 'Reset Password | The UNFILTERED COLLECTIVE'
      description = isEs ? 'Restablece la contraseña de tu cuenta.' : 'Reset your account password.'
    } else if (path.endsWith('/search')) {
      title = isEs 
        ? 'Buscar Invitado - RSVP | The UNFILTERED COLLECTIVE' 
        : 'Find Your Invitation - RSVP | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Búscate por tu nombre y apellido para confirmar tu asistencia a la boda.' 
        : 'Search by your name to confirm your attendance to the wedding.'
    } else if (path.includes('/form/')) {
      title = isEs 
        ? 'Formulario RSVP - Confirmar Asistencia | The UNFILTERED COLLECTIVE' 
        : 'RSVP Form - Confirm Attendance | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Completa tus datos dietéticos y confirma tu asistencia para la boda.' 
        : 'Fill in your dietary requirements and confirm your attendance for the wedding.'
    } else if (path.startsWith('/rsvp')) {
      title = isEs 
        ? 'Confirmar Asistencia - RSVP de Boda | The UNFILTERED COLLECTIVE' 
        : 'RSVP - Wedding Invitation | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Confirma tu asistencia y completa los detalles para celebrar con nosotros.' 
        : 'Confirm your attendance and submit details to celebrate with us.'
    } else if (path.startsWith('/lifesaver-link')) {
      title = isEs ? 'Enlace Salvavidas | The UNFILTERED COLLECTIVE' : 'Lifesaver Link | The UNFILTERED COLLECTIVE'
      description = isEs 
        ? 'Completa tus datos de contacto para la lista de invitados.' 
        : 'Submit your contact details for the guest list.'
    } else {
      // Home / Fallback
      title = isEs 
        ? 'The UNFILTERED COLLECTIVE - Organización de Bodas sin Filtros | Agenda, Presupuesto y Herramientas' 
        : 'The UNFILTERED COLLECTIVE - Finally, a Wedding Tool That Gets Real'
      description = isEs 
        ? 'La planificación de tu boda, sin filtros ni caos. Agenda de boda, presupuesto, invitados, cronograma y herramientas gratis creadas por profesionales de bodas.' 
        : 'Plan your wedding without the chaos. A comprehensive wedding planning platform built by wedding pros, for real couples. Manage your budget, guests, vendors, and timeline.'
      keywords = isEs
        ? 'organización de bodas, planificador de bodas gratis, agenda de boda online, presupuesto boda, invitaciones de boda, rsvp boda, gestor de invitados, bodas y eventos'
        : 'wedding planning, wedding organizer, wedding checklist, wedding budget, wedding RSVP, wedding management, wedding planning tool, wedding website'
    }

    if (title) {
      document.title = title
      const metaTitle = document.querySelector('meta[name="title"]')
      if (metaTitle) metaTitle.setAttribute('content', title)
      const ogTitle = document.querySelector('meta[property="og:title"]')
      if (ogTitle) ogTitle.setAttribute('content', title)
    }
    
    if (description) {
      const metaDesc = document.querySelector('meta[name="description"]')
      if (metaDesc) metaDesc.setAttribute('content', description)
      const ogDesc = document.querySelector('meta[property="og:description"]')
      if (ogDesc) ogDesc.setAttribute('content', description)
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]')
    if (metaKeywords) {
      metaKeywords.setAttribute('content', keywords || (isEs 
        ? 'organización de bodas, planificador de bodas, agenda de boda, presupuesto de boda, invitaciones de boda, rsvp boda' 
        : 'wedding planning, wedding organizer, wedding checklist, wedding budget, wedding RSVP, wedding management'))
    }
  }, [location.pathname, language])

  if (authLoading) {
    return <Loader />
  }

  return (
    <div className="app">
      <DialogContainer />
      <Routes>
        <Route path="/lifesaver-link/:token" element={
          <Suspense fallback={<Loader />}>
            <LifesaverForm />
          </Suspense>
        } />
        
        <Route path="/rsvp/:slug" element={
          <Suspense fallback={<Loader />}>
            <RSVPInvitePage />
          </Suspense>
        } />
        <Route path="/rsvp/:slug/search" element={
          <Suspense fallback={<Loader />}>
            <RSVPSearch />
          </Suspense>
        } />
        <Route path="/rsvp/:slug/form/:guestId" element={
          <Suspense fallback={<Loader />}>
            <RSVPForm />
          </Suspense>
        } />
        
        <Route path="*" element={
          <>
            <Navbar />
            <main>
              <Suspense fallback={<Loader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about-us" element={<AboutUs />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/pricing" element={<Pricing />} />
                  
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<DashboardHome />} />
                    <Route path="checklist" element={<Checklist />} />
                    <Route path="budget-manager" element={<BudgetManager />} />
                    
                    <Route path="guest-manager" element={<GuestManager />}>
                      <Route index element={<GuestManagerIndex />} />
                      <Route path="the-lifesaver-link" element={<TheLifesaverLink />} />
                      <Route path="rsvp-form-builder" element={<RSVPFormBuilder />} />
                      <Route path="craft-your-invite" element={<CraftYourInvite />} />
                      <Route path="gentle-reminder-time" element={<GentleReminderTime />} />
                      <Route path="who-sits-where" element={<WhoSitsWhere />} />
                    </Route>
                    
                    <Route path="supplier-manager" element={<SupplierManager />}>
                      <Route index element={<SupplierManagerIndex />} />
                      <Route path="numbers-please" element={<NumbersPlease />} />
                      <Route path="the-chosen-ones" element={<TheChosenOnes />} />
                    </Route>
                    
                    <Route path="timeline-creator" element={<TimelineCreator />} />
                    
                    <Route path="lists" element={<Lists />}>
                      <Route index element={<ListsIndex />} />
                      <Route path="must-capture-moments" element={<MustCaptureMoments />} />
                      <Route path="the-soundtrack" element={<TheSoundtrack />} />
                      <Route path="bloom-board" element={<BloomBoard />} />
                      <Route path="wedding-cargo" element={<WeddingCargo />} />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
            </main>
            {!isDashboard && <Footer />}
          </>
        } />
      </Routes>
    </div>
  )
}

export default App
